import React, { useMemo, useState } from "react";
// Components
import MultiSelect from "view/components/Multiselect";
import brandPlaceholder from "assets/images/Placeholders/brandPlaceholderImg.png";
import CustomToolTip from "view/components/ToolTip";
import { IconButton } from "@mui/material";
import {
  InformationCircleIcon,
  PauseIcon,
  RefreshIcon,
} from "assets/icons/HeroIcons";
import apiLibrary from "services/api";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { IfetchBrand } from "store/brandDetail/initialState";
import { Toasts } from "view/components/Toasts";
import { fetchSingleBrandAction } from "store/brandDetail";
import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { useDispatch } from "react-redux";
import { setLoadingProgress } from "store/loadingBar";
import { getRandomProgress } from "utils/getRandomProgress";

const RELEVANT_DNS_STATUSES = ["paused", "completed", "pending"] as const;

type RelevantDnsStatus = (typeof RELEVANT_DNS_STATUSES)[number];

const ALL_DNS_STATUSES = [
  "archive",
  "public",
  "active",
  "completed",
  "pending",
  "paused",
  // "inProgress",
] as const;

type DnsStatus = (typeof ALL_DNS_STATUSES)[number];

const getDnsStatusColor = (status: string): string => {
  switch (status as DnsStatus) {
    case "archive":
      return "bg-primaryExtraLight text-primaryMid";
    case "public":
    case "active":
    case "completed":
      return "bg-tertiaryExtraLight text-tertiaryMid";
    case "pending":
      return "bg-accent_1Light text-accent_1Dark";
    case "paused":
      return "text-accent_tertiary_red bg-accent_1LightExtraMid";
    // case "inProgress":
    //   return "text-[#e72121] bg-[#ffeded]";
    default:
      return "white";
  }
};

const getTooltipText = (status: RelevantDnsStatus): string => {
  switch (status) {
    case "completed":
      return "DNS propagation is complete. The brand is live and ready to use!";
    case "paused":
      return "The DNS propagation checks are paused because they either timed out or were manually stopped. Click Restart to resume checking the DNS propagation status.";
    case "pending":
      return "DNS propagation is still in progress, which means the brand isn’t live yet. To complete the process, add the required DNS records in the domain manager. If this has not been set up yet, pause the propagation checks to save system resources. Once the DNS values have been added, restart the propagation check to continue.";
    default:
      return "";
  }
};

export const ViewOrganization: React.FC<{ values: any }> = ({ values }) => {
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();

  const { data: BrandData, stateIs } = useSelector<RootState, IfetchBrand>(
    (state) => state?.singleBrand
  );

  const [isLoading, setIsLaoding] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const rawDnsStatus = values?.dnsPropagationStatus as string | undefined;
  const dnsStatus = rawDnsStatus ?? "";
  const isRelevantStatus = useMemo(
    () => RELEVANT_DNS_STATUSES.includes(dnsStatus as RelevantDnsStatus),
    [dnsStatus]
  );

  const toggleTooltip = () => setShowTooltip((prev) => !prev);

  const statusColorClass = useMemo(
    () => getDnsStatusColor(dnsStatus),
    [dnsStatus]
  );

  const tooltipText = useMemo(
    () =>
      isRelevantStatus ? getTooltipText(dnsStatus as RelevantDnsStatus) : "",
    [dnsStatus, isRelevantStatus]
  );

  const showActionButton = ["paused", "pending"].includes(dnsStatus);

  const actionButtonText = dnsStatus === "paused" ? "Restart" : "Pause";
  const ActionIcon = dnsStatus === "paused" ? RefreshIcon : PauseIcon;

  const pauseOrRestartThePropagation = async (status = "pause") => {
    try {
      dispatch(setLoadingProgress(getRandomProgress()));
      setIsLaoding(true);
      // Perform the API call to manage domain propagation
      const { data, message } = await apiLibrary.Brands.manageDomainPropagation(
        BrandData.brand?.id,
        status
      );


      // If the API call is successful, show a success message
      if (data) {
        // Success toast message
        Toasts.success(message);
        console.log(`Domain propagation ${status} successfully.`);
      }

      if (BrandData.brand?.id) {
        // setTimeout(() => {
          dispatch(
            fetchSingleBrandAction(BrandData.brand?.id?.toString() ?? "")
          );
        // }, 1000);
      }
    } catch (error: any) {
      // Handle different types of errors (e.g., network issues, API errors, etc.)

      // Default error message
      let errorMessage = "An unexpected error occurred. Please try again.";

      // Check if the error is an AxiosError (or related network error)
      if (error.response) {
        // API responded with an error status code
        errorMessage =
          error.response?.data?.message ||
          error.response.statusText ||
          "API Error occurred";
      } else if (error.request) {
        // No response was received (network error)
        errorMessage =
          "No response received from the server. Please check your network connection.";
      } else {
        // General error while setting up the request
        errorMessage = error.message || "Error in request setup.";
      }

      // Log the error to the console for debugging purposes
      console.error("Error occurred while managing domain propagation:", error);

      // Toast the original error message to the user
      Toasts.error(errorMessage);
    } finally {
      // setTimeout(() => {
        setIsLaoding(false);
        dispatch(setLoadingProgress(100));
      // }, 1000);
    }
  };

  return (
    <div className="grid py-4 gap-6">
      {/* Organization (Full Row) */}
      <div className="flex items-start col-span-full space-x-4">
        <div className="relative">
          <img
            src={values.organizationImage?.file?.thumb || brandPlaceholder}
            alt="Organization"
            className="w-12 h-12 object-contain"
          />
        </div>
        <div className="mt-1">
          <p className="text-sm font-Overpass text-textMidLight dark:text-caption">
            Organization
          </p>
          <p className="text-sm font-Overpass text-textNormal dark:text-tertiaryLight">
            {values.organizationId?.label}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Admin URL */}
        <div className="space-y-1">
          <p className="text-sm font-Overpass text-textMidLight dark:text-caption">
            Admin URL
          </p>
          <p className="text-sm font-Overpass text-textNormal dark:text-tertiaryLight break-words pr-5">
            {values?.adminUrl}
          </p>
        </div>

        {/* App URL */}
        <div className="space-y-1">
          <p className="text-sm font-Overpass text-textMidLight dark:text-caption">
            App URL
          </p>
          <p className="text-sm font-Overpass text-textNormal dark:text-tertiaryLight break-words pr-5">
            {values?.appUrl}
          </p>
        </div>
      </div>

      {/* DNS Propagation Status */}
      <div className="flex justify-start items-start gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-Overpass text-textMidLight dark:text-caption">
              DNS Propagation Status
            </p>
            {isRelevantStatus && (
              <CustomToolTip
                title={tooltipText}
                showTooltip={showTooltip}
                toggleTooltip={toggleTooltip}
              >
                <IconButton onClick={toggleTooltip}>
                  <InformationCircleIcon className="w-5 h-5 fill-[#475569]" />
                </IconButton>
              </CustomToolTip>
            )}
          </div>
          <p
            className={`text-[12px] font-Overpass py-1 px-2 rounded-lg capitalize max-w-max ${statusColorClass}`}
          >
            {dnsStatus}
          </p>
        </div>
        {showActionButton && (
          <button
            type="button"
            disabled={isLoading || stateIs === "Pending"}
            onClick={() => {
              pauseOrRestartThePropagation(
                dnsStatus === "paused" ? "pending" : "paused"
              );
            }}
            className={`flex items-center gap-1.5 h-12 px-3 rounded-2xl bg-[#005c89] mt-2 self-start disabled:cursor-not-allowed ${isLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            <ActionIcon fill="white" className="w-5 h-5" />
            <p className="text-base font-semibold text-textWhite">
              {actionButtonText}
            </p>
          </button>
        )}
      </div>
    </div>
  );
};
